<script setup>
import { ref } from 'vue'
import { lookupPublicTicketRequest } from '../services/publicLotteryService.js'
import {
  toMaskedTicket,
  toPublicErrorMessage,
  toPublicPaymentMessage,
  toPublicPaymentStatusLabel,
  toPublicSupportReference,
} from '../utils/publicUi.js'

const reference = ref('')
const loading = ref(false)
const error = ref('')
const supportReference = ref('')
const warning = ref('')
const result = ref(null)

const search = async () => {
  loading.value = true
  error.value = ''
  warning.value = ''
  supportReference.value = ''
  result.value = null

  try {
    const data = await lookupPublicTicketRequest(reference.value)
    result.value = {
      lotteryName: data?.lottery_name || 'Lottery',
      status: data?.status || 'unknown',
      ticket: toMaskedTicket(data?.ticket_number || data?.reference),
      drawComplete: data?.draw_complete === true,
      winner: data?.winner === true,
      winnerLabel: data?.winner_label || 'Winner information available',
      paymentStatus: toPublicPaymentStatusLabel(data?.payment_status),
      paymentMessage: toPublicPaymentMessage(data?.payment_status),
      stale: data?.stale === true,
      duplicateConflict: data?.duplicate_ownership_conflict === true,
    }

    if (result.value.duplicateConflict) {
      warning.value = 'We found conflicting ticket ownership records. Please contact support for verification.'
    } else if (!result.value.drawComplete) {
      warning.value = 'Draw is not completed yet. Please check back later.'
    } else if (result.value.stale) {
      warning.value = 'Results may be updating. Please retry shortly for the latest status.'
    }
  } catch (err) {
    error.value = toPublicErrorMessage(err, 'Ticket lookup is currently unavailable.')
    supportReference.value = toPublicSupportReference(err)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <section class="public-view">
    <h1>Ticket Lookup</h1>
    <p class="muted">Enter your ticket reference to view draw status.</p>

    <form class="lookup-row" @submit.prevent="search">
      <input v-model="reference" type="text" required placeholder="Ticket reference" />
      <button class="btn" :disabled="loading">{{ loading ? 'Checking...' : 'Lookup' }}</button>
    </form>

    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="supportReference" class="warning">Support reference: {{ supportReference }}</p>
    <p v-if="warning" class="warning">{{ warning }}</p>

    <article v-if="result" class="card">
      <h2>{{ result.lotteryName }}</h2>
      <p><strong>Ticket:</strong> {{ result.ticket }}</p>
      <p><strong>Status:</strong> {{ result.status }}</p>
      <p><strong>Payment:</strong> {{ result.paymentStatus }}</p>
      <p><strong>Draw result:</strong> {{ result.drawComplete ? (result.winner ? 'Winning ticket' : 'Not marked as winner') : 'Pending draw' }}</p>
      <p v-if="result.drawComplete && result.winner">{{ result.winnerLabel }}</p>
      <p v-if="result.paymentMessage" class="muted">{{ result.paymentMessage }}</p>
    </article>
  </section>
</template>

<style scoped>
.public-view { max-width: 640px; margin: 0 auto; padding: 1rem; display: grid; gap: 0.75rem; }
.lookup-row { display: grid; gap: 0.5rem; grid-template-columns: 1fr auto; }
input { border: 1px solid #d1d5db; border-radius: 6px; min-height: 44px; padding: 0.45rem; }
.btn { border: none; border-radius: 6px; background: #2563eb; color: #fff; padding: 0.45rem 0.75rem; min-height: 44px; }
.card { border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 0.75rem; }
.muted { color: #6b7280; }
.warning { color: #92400e; background: #fffbeb; border-radius: 6px; padding: 0.45rem; }
.error { color: #991b1b; background: #fee2e2; border-radius: 6px; padding: 0.45rem; }
@media (max-width: 640px) { .lookup-row { grid-template-columns: 1fr; } }
</style>

